import SessionHelper from "./SessionHelper";

/**
 * Helper class to manage file upload / download via Azure Storage
 * */
class StorageHelper {
  // Session state variables
  static _folderName = SessionHelper.getSessionId();
  static _fileName = null;

  static _apiBaseUrl = process.env.REACT_APP_POINTR_MAPSCALE_DEMO_API_BASE_URL;

  static upload = async (file, onSuccess, onError, onProgress) => {
    try {
      if (!file) {
        if (onError) {
          onError("No file found - cannot upload");
        }
        return;
      }

      const reader = new FileReader();
      reader.onloadend = async () => {
        const folderName = StorageHelper._folderName;
        const fileName = StorageHelper.sanitizeString(file.name);
        StorageHelper._fileName = fileName;
        const base64String = reader.result.split(",")[1]; // Extract base64 content

        const xhr = new XMLHttpRequest();
        xhr.open(
          "POST",
          StorageHelper._apiBaseUrl + "/api/storage-files",
          true
        );

        // Set headers
        xhr.setRequestHeader("Content-Type", "application/json");

        // Track progress
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable && onProgress) {
            const progress = Math.round((event.loaded / event.total) * 100);
            onProgress(progress);
          }
        };

        // Handle success/failure
        xhr.onload = () => {
          if (xhr.status === 200) {
            const data = JSON.parse(xhr.responseText);
            if (data.status === 200) {
              if (onSuccess) onSuccess();
            } else {
              if (onError) onError(data.body.error);
            }
          } else {
            if (onError) onError("Error uploading file");
          }
        };

        xhr.onerror = () => {
          if (onError) onError("Failed to upload file");
        };

        // Send the request
        xhr.send(
          JSON.stringify({
            folderName,
            fileName,
            fileContent: base64String,
          })
        );
      };

      reader.readAsDataURL(file); // Convert the file to base64
    } catch (exc) {
      console.error("Exception while trying to upload the file - " + exc);
      if (onError) {
        onError(exc);
      }
    }
  };

  /**
   * Read the uploaded file and get a download URL (and file extenion) for it
   * */
  static read = async (onSuccess, onError) => {
    try {
      const folderName = StorageHelper._folderName;
      const fileName = StorageHelper._fileName;

      const request = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          foldername: folderName,
          filename: fileName,
          shoulddownload: false,
        },
      };

      let response;
      try {
        response = await fetch(
          StorageHelper._apiBaseUrl + "/api/storage-files",
          request
        );
      } catch (exc) {
        console.error(
          "Exception while trying to read file from storage - " + exc
        );
        if (onError) {
          onError(exc);
        }
        return;
      }

      const data = await response.json();

      if (data.status === 200) {
        if (onSuccess) {
          // TODO: Handle invalid data
          const ext = StorageHelper.getFileExtension(fileName);
          onSuccess(data.body.secureLink, ext);
        }
      } else {
        console.error("Error reading file from storage: ", data.body.error);
        if (onError) {
          onError(data);
        }
      }
    } catch (exc) {
      console.error(
        "Exception while trying to read file from storage - " + exc
      );
      if (onError) {
        onError(exc);
      }
    }
  };

  /**
   * Downloads the file at specified path (folder + file)
   * */
  static download = async (
    onSuccess,
    onError,
    folderNameParam,
    fileNameParam
  ) => {
    try {
      const folderName = folderNameParam;
      const fileName = fileNameParam;

      const request = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          foldername: folderName,
          filename: fileName,
          shoulddownload: true,
        },
      };

      let response;
      try {
        response = await fetch(
          StorageHelper._apiBaseUrl + "/api/storage-files",
          request
        );
      } catch (exc) {
        console.error(
          "Exception while trying to download file from storage - " + exc
        );
        if (onError) {
          onError(exc);
        }
        return;
      }

      const data = await response.json();

      if (data.status === 200) {
        if (onSuccess) {
          // TODO: Handle invalid data
          onSuccess(data.body.file64);
        }
      } else {
        console.error("Error reading file from storage: ", data.body.error);
        if (onError) {
          onError(data);
        }
      }
    } catch (exc) {
      console.error(
        "Exception while trying to read file from storage - " + exc
      );
      if (onError) {
        onError(exc);
      }
    }
  };

  /**
   * Logs email address as a text file in Storage
   * */
  static logEmail = async (email, onSuccess, onError) => {
    try {
      if (!email) {
        if (onError) {
          onError("No email provided - cannot log");
        }
        return;
      }

      // Pass email to the API
      const folderName = StorageHelper._folderName;
      let response;
      try {
        response = await fetch(
          StorageHelper._apiBaseUrl + "/api/storage-emails",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              folderName,
              email,
            }),
          }
        );
      } catch (exc) {
        console.error("Exception while trying to log email - " + exc);
        if (onError) {
          onError(exc);
        }
        return;
      }

      const data = await response.json();
      if (response.ok) {
        if (onSuccess) {
          onSuccess();
        }
      } else {
        console.error("Error uploading file to storage: ", data.body.error);
        if (onError) {
          onError(data.body.error);
        }
      }
    } catch (exc) {
      console.error("Exception while trying to upload the file - " + exc);
      if (onError) {
        onError(exc);
      }
    }
  };

  /**
   * Gets email address from Storage
   * */
  static getEmail = async (onSuccess, onError) => {
    try {
      let response;
      try {
        response = await fetch(
          StorageHelper._apiBaseUrl + "/api/storage-emails",
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              foldername: StorageHelper._folderName,
            },
          }
        );
      } catch (exc) {
        console.error(
          "Exception while trying to read email from storage - " + exc
        );
        if (onError) {
          onError(exc);
        }
        return;
      }

      const data = await response.json();
      if (response.ok) {
        if (onSuccess) {
          // TODO: Handle invalid data
          onSuccess(data.body.email);
        }
      } else {
        console.error("Error reading email from storage: ", data.body.error);
        if (onError) {
          onError(data.body.error);
        }
      }
    } catch (exc) {
      console.error(
        "Exception while trying to read email from storage - " + exc
      );
      if (onError) {
        onError(exc);
      }
    }
  };

  ////////////
  // Helper methods
  ////////////

  // Make String more code friendly (eg. remove spaces)
  static sanitizeString(fileName) {
    return fileName
      .trim() // Remove leading and trailing spaces
      .toLowerCase() // Convert to lowercase
      .replace(/\s+/g, "-") // Replace spaces with dashes
      .replace(/[^a-z0-9\-_.]/g, "") // Remove all non-alphanumeric characters except dashes, underscores, and periods
      .replace(/-+/g, "-") // Replace multiple dashes with a single dash
      .replace(/_+/g, "_"); // Replace multiple underscores with a single underscore
  }

  // Get file extension from a file name (eg. "dxf" from "my-file.dxf")
  static getFileExtension(fileName) {
    const index = fileName.lastIndexOf(".");
    return index !== -1 ? fileName.substring(index + 1) : "";
  }
}

export default StorageHelper;
